import { isEmbeddedInIframe } from '@/shared/embed/is-embedded';
import { isAllowedCockpitImpersonationParentOrigin } from '@/shared/embed/cockpit-embed-origins';
import { clearSessionImpersonatedUserId, writeSessionImpersonatedUserId } from '@/shared/utils/impersonation-storage';

const IMPERSONATE_QUERY_PARAM = '_impersonate';

/**
 * Firebase UIDs are typically 28 alphanumeric chars; cap at 128 for safety. Reject anything
 * containing characters that would be unsafe to forward in a downstream HTTP header (CRLF, control
 * chars, whitespace, etc.).
 */
const UID_PATTERN = /^[A-Za-z0-9]{1,128}$/;

function stripImpersonateParamFromUrl(): void {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(IMPERSONATE_QUERY_PARAM)) {
            return;
        }
        url.searchParams.delete(IMPERSONATE_QUERY_PARAM);
        const qs = url.searchParams.toString();
        const newUrl = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
        window.history.replaceState(null, '', newUrl);
    } catch {
        /* ignore */
    }
}

/**
 * When RAM runs inside a Cockpit iframe, Cockpit cannot write to RAM's localStorage (cross-origin).
 * Cockpit therefore appends `?_impersonate=<uid>` (or empty to clear) to the iframe `src`.
 *
 * Must run synchronously **before** any HTTP client / API call so the very first request already
 * includes the `x-impersonate-user` header (read from session/localStorage by `client.ts`).
 *
 * Hardening:
 *  - Only honored inside an iframe.
 *  - Parent must be an allowlisted Cockpit origin (`document.referrer`-based; fail closed when
 *    Referrer-Policy strips the referrer). Without this, any third-party site could embed RAM and
 *    poison the impersonation header for the victim's RAM origin.
 *  - Value must match a strict Firebase-UID pattern; otherwise treated as "clear".
 *  - Stored in **sessionStorage** (tab-scoped) so a value injected via iframe cannot leak into
 *    another standalone RAM tab on the same origin.
 *  - URL query param is stripped via `history.replaceState` to avoid leaking the uid via `Referer`
 *    to third parties or via bookmarks/history.
 *
 *  Note: this is defense-in-depth. The authoritative impersonation check must happen server-side
 *  (the authenticated principal must hold the impersonation permission); the frontend is never the
 *  security boundary.
 */
export function applyImpersonationFromIframeUrl(): void {
    if (!isEmbeddedInIframe()) {
        return;
    }
    if (!isAllowedCockpitImpersonationParentOrigin(document.referrer)) {
        return;
    }

    let params: URLSearchParams;
    try {
        params = new URLSearchParams(window.location.search);
    } catch {
        return;
    }
    if (!params.has(IMPERSONATE_QUERY_PARAM)) {
        return;
    }

    const raw = params.get(IMPERSONATE_QUERY_PARAM) ?? '';
    if (raw.length === 0) {
        writeSessionImpersonatedUserId(null);
    } else if (UID_PATTERN.test(raw)) {
        writeSessionImpersonatedUserId(raw);
    } else {
        console.warn('Ignoring malformed _impersonate value from iframe URL');
        clearSessionImpersonatedUserId();
    }

    stripImpersonateParamFromUrl();
}
